import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  FileImage,
  Loader2,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { ActiveTab } from './types';

export interface PostPreviewHeaderProps {
  isInline: boolean;
  activeTab: ActiveTab;
  onTabChange: (tab: ActiveTab) => void;
  topic?: string;
  date?: string;
  time?: string;
  isEditingTopic: boolean;
  editedTopic: string;
  onTopicChange: (topic: string) => void;
  onTopicBlur: () => void;
  onTopicEditStart: () => void;
  topicInputRef: React.RefObject<HTMLInputElement | null>;

  // Navigation (optional - only for calendar with multiple posts)
  showNavigation?: boolean;
  onNavigatePrev?: () => void;
  onNavigateNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;

  // Actions
  isExporting: boolean;
  onExportPNG: () => void;
  onSchedule?: () => void;
  isScheduled?: boolean;
  onClose?: () => void;
}

export function PostPreviewHeader({
  isInline,
  activeTab,
  onTabChange,
  topic,
  date,
  time,
  isEditingTopic,
  editedTopic,
  onTopicChange,
  onTopicBlur,
  onTopicEditStart,
  topicInputRef,
  showNavigation = false,
  onNavigatePrev,
  onNavigateNext,
  hasPrev = false,
  hasNext = false,
  isExporting,
  onExportPNG,
  onSchedule,
  isScheduled = false,
  onClose,
}: PostPreviewHeaderProps) {
  return (
    <div className={`sticky top-0 z-10 flex-shrink-0 px-6 border-b bg-background ${isInline ? 'pt-3 pb-3' : 'pt-4 pb-3'}`}>
      {isInline ? (
        /* Inline Mode: Single horizontal row */
        <div className="flex items-center justify-between gap-3">
          {/* Left: Preview/Edit Tabs (only shown on mobile < 700px, hidden when side-by-side) */}
          <Tabs value={activeTab} onValueChange={(v) => onTabChange(v as ActiveTab)} className="[@media(min-width:700px)]:hidden">
            <TabsList className="h-8">
              <TabsTrigger value="preview" className="text-xs px-3">Preview</TabsTrigger>
              <TabsTrigger value="edit" className="text-xs px-3">Edit</TabsTrigger>
            </TabsList>
          </Tabs>

          {/* Center: Navigation arrows (if enabled) */}
          {showNavigation && (
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="icon-sm"
                onClick={onNavigatePrev}
                disabled={!hasPrev}
                title="Previous post"
                className="h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={onNavigateNext}
                disabled={!hasNext}
                title="Next post"
                className="h-8 w-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Right: Export + Schedule + Close */}
          <div className="flex items-center gap-2">
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting} className="h-8">
                  {isExporting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={onExportPNG} disabled={isExporting}>
                  <FileImage className="h-4 w-4 mr-2" />
                  Export as PNG
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Schedule Button */}
            {onSchedule && (
              isScheduled ? (
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-500 px-2">
                  <Clock className="h-4 w-4" />
                  <span className="hidden sm:inline text-xs">Scheduled</span>
                </div>
              ) : (
                <Button onClick={onSchedule} variant="success" size="sm" className="h-8">
                  <Clock className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline text-xs">Schedule</span>
                </Button>
              )
            )}

            {/* Close Button (if provided) */}
            {onClose && (
              <>
                <div className="h-6 w-px bg-border mx-1" />
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={onClose}
                  title="Close"
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      ) : (
        /* Fullscreen Mode: Three equal columns for true centering */
        <div className="grid grid-cols-3 items-center gap-4">
          {/* Left: Topic + Date */}
          <div className="flex items-center gap-2 min-w-0">
            {isEditingTopic ? (
              <input
                ref={topicInputRef}
                type="text"
                value={editedTopic}
                onChange={(e) => onTopicChange(e.target.value)}
                onBlur={onTopicBlur}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === 'Escape') {
                    onTopicBlur();
                  }
                }}
                className="text-base font-semibold bg-transparent border-b border-primary focus:outline-none flex-1 min-w-0"
              />
            ) : (
              <div
                onClick={onTopicEditStart}
                className="cursor-pointer hover:text-primary transition-colors group flex items-center gap-2 min-w-0"
                title="Click to edit topic"
              >
                <DialogTitle className="inline-flex items-center gap-2 truncate text-base group-hover:underline decoration-2 underline-offset-4 decoration-primary/50">
                  {topic || 'Post Preview'}
                </DialogTitle>
              </div>
            )}
            {!isEditingTopic && date && time && (
              <>
                <span className="text-muted-foreground">•</span>
                <span className="text-sm text-muted-foreground whitespace-nowrap">
                  {date} at {time}
                </span>
              </>
            )}
          </div>

          {/* Center: Navigation Arrows (if enabled, truly centered) */}
          {showNavigation && (
            <div className="flex items-center gap-2 justify-center">
              <Button
                variant="secondary"
                size="icon-sm"
                onClick={onNavigatePrev}
                disabled={!hasPrev}
                title="Previous post"
                className="h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon-sm"
                onClick={onNavigateNext}
                disabled={!hasNext}
                title="Next post"
                className="h-8 w-8"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Right: Main CTAs */}
          <div className="flex items-center gap-2 justify-end">
            {/* Export Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" disabled={isExporting} className="h-8">
                  {isExporting ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span className="hidden sm:inline ml-2 text-xs">Exporting...</span>
                    </>
                  ) : (
                    <>
                      <Download className="h-4 w-4" />
                      <span className="hidden sm:inline ml-2 text-xs">Export</span>
                      <ChevronDown className="h-3 w-3 ml-1" />
                    </>
                  )}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-44">
                <DropdownMenuItem onClick={onExportPNG} disabled={isExporting}>
                  <FileImage className="h-4 w-4 mr-2" />
                  Export as PNG
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Schedule Button */}
            {onSchedule && (
              isScheduled ? (
                <div className="flex items-center gap-2 text-sm text-amber-600 dark:text-amber-500 px-2">
                  <Clock className="h-4 w-4" />
                  <span className="hidden sm:inline text-xs">Scheduled</span>
                </div>
              ) : (
                <Button onClick={onSchedule} variant="success" size="sm" className="h-8">
                  <Clock className="h-4 w-4 sm:mr-2" />
                  <span className="hidden sm:inline text-xs">Schedule</span>
                </Button>
              )
            )}

            {/* Close Button (if provided) */}
            {onClose && (
              <>
                <div className="h-6 w-px bg-border mx-1" />
                <Button
                  variant="outline"
                  size="icon-sm"
                  onClick={onClose}
                  title="Close"
                  className="h-8 w-8"
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
